import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output, inject, DestroyRef } from '@angular/core';
import { ChartRolloverMaintenanceService } from './chart-rollover-maintenance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

/**
 * Angular 12 migration of admin/chart-rollover-maintenance.
 * Replicates: load 24naqb (chartKeyList), save b5cgu4 (clob_data/clob_settings).
 */
@Component({
    selector: 'gtw-chart-rollover-maintenance',
    templateUrl: './chart-rollover-maintenance.component.html',
    styleUrls: ['./chart-rollover-maintenance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChartRolloverMaintenanceComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  filterParams: any = {};
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  modalTitle = 'Chart Roll Over';
  private isInitialized = false;
  private isLoading = false;

  chartKeyList: any[] = [];
  chartRoll: {
    TAX_YEAR: string;
    PRIOR_YR_KEY: string | { CHART_KEY: string; CHART_CODE: string };
    CURR_YR_CHART_CODE: string;
  } = {
    TAX_YEAR: '',
    PRIOR_YR_KEY: '',
    CURR_YR_CHART_CODE: ''
  };
  chartRollBackup: any = null;
  isEdit = false;
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  TAX_YEAR_NOT_VALID = false;
  PRIOR_YR_KEY_KEY_NOT_VALID = false;
  CURR_YR_CHART_CODE_KEY_NOT_VALID = false;

  @Input('base-urls')
  set _base_urls(v: string) {
    const u = safeJsonInput<any>(v, {}, 'chart-rollover-maintenance.base-urls') || {};
    if (Object.keys(u).length > 0) { this.baseURLs = u; this.tryInit(); }
  }

  @Input('header-data')
  set _header_data(v: string) {
    const h = safeJsonInput<any>(v, {}, 'chart-rollover-maintenance.header-data') || {};
    if (Object.keys(h).length > 0) {
      this.globalService = h;
      this.globalService.globalParams = h.globalParams || h.globalparams || {};
      this.GENERAL_CONFIG = h.generalConfigData;
      this.tryInit();
    }
  }

  @Input('row-data')
  set _row_data(v: string) {
    this.rowData = safeJsonInput<any>(v, {}, 'chart-rollover-maintenance.row-data') || {};
    this.tryInit();
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    this.filterParams = safeJsonInput<any>(v, {}, 'chart-rollover-maintenance.filter-params') || {};
    this.tryInit();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private chartRolloverService: ChartRolloverMaintenanceService,
    private gtwSnackbar: GtwSnackbarService
  ) {}

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    this.isInitialized = true;
    this.loadData();
    this.cdr.detectChanges();
  }

  private loadData(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (!baseUrl) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;
    this.cdr.detectChanges();

    this.chartRolloverService.loadChartKeyList(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data && data.callSuccess === '1' && data.jsonObject) {
          this.chartKeyList = Array.isArray(data.jsonObject) ? data.jsonObject : [];
        }
        const taxYear = this.filterParams?.tax_year ?? this.globalService?.globalParams?.tax_year ?? this.rowData?.TAX_YEAR ?? '';
        if (this.rowData && Object.keys(this.rowData).length > 0) {
          this.isEdit = true;
          this.chartRoll = {
            TAX_YEAR: this.rowData.TAX_YEAR ?? taxYear,
            PRIOR_YR_KEY: this.rowData.PRIOR_YR_KEY ?? '',
            CURR_YR_CHART_CODE: this.rowData.CURR_YR_CHART_CODE ?? ''
          };
        } else {
          this.chartRoll = {
            TAX_YEAR: taxYear,
            PRIOR_YR_KEY: '',
            CURR_YR_CHART_CODE: ''
          };
        }
        this.chartRollBackup = { ...this.chartRoll };
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  isValid(): boolean {
    this.TAX_YEAR_NOT_VALID = !this.chartRoll.TAX_YEAR;
    const priorKey = this.chartRoll.PRIOR_YR_KEY;
    const priorKeyStr = typeof priorKey === 'object' && priorKey && (priorKey as any).CHART_KEY != null
      ? (priorKey as any).CHART_KEY
      : String(priorKey || '');
    this.PRIOR_YR_KEY_KEY_NOT_VALID = !priorKeyStr;
    this.CURR_YR_CHART_CODE_KEY_NOT_VALID = !this.chartRoll.CURR_YR_CHART_CODE;
    this.cdr.detectChanges();
    return !this.TAX_YEAR_NOT_VALID && !this.PRIOR_YR_KEY_KEY_NOT_VALID && !this.CURR_YR_CHART_CODE_KEY_NOT_VALID;
  }

  save(): void {
    if (this.crudLoading) return;
    if (!this.isValid()) {
      this.gtwSnackbar.openSnackBar('Please fill all the required fields', 'gtw-snackbar--yellow');
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const priorKey = this.chartRoll.PRIOR_YR_KEY;
    const priorKeyStr = typeof priorKey === 'object' && priorKey && (priorKey as any).CHART_KEY != null
      ? (priorKey as any).CHART_KEY
      : String(priorKey || '');
    const taxYearNum = parseInt(String(this.filterParams?.tax_year ?? this.globalService?.globalParams?.tax_year ?? this.chartRoll.TAX_YEAR ?? '0'), 10) || 0;
    const clobData = {
      PRIOR_YR_KEY: priorKeyStr,
      CURR_YR_CHART_CODE: this.chartRoll.CURR_YR_CHART_CODE || ''
    };
    const clobSettings = { tax_year: taxYearNum + 1 };

    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (!baseUrl) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.chartRolloverService.saveChartRollover(
      baseUrl,
      clobData,
      clobSettings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (data && data.callSuccess === '1' && data.errorMessage === 'null') {
          this.gtwSnackbar.openSnackBar('Chart Roll Over Created successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbar.openSnackBar(data?.errorMessage || 'Save failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    const taxYear = this.filterParams?.tax_year ?? this.globalService?.globalParams?.tax_year ?? '';
    this.chartRoll = {
      TAX_YEAR: taxYear,
      PRIOR_YR_KEY: '',
      CURR_YR_CHART_CODE: ''
    };
    this.TAX_YEAR_NOT_VALID = false;
    this.PRIOR_YR_KEY_KEY_NOT_VALID = false;
    this.CURR_YR_CHART_CODE_KEY_NOT_VALID = false;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  changePriorYearKey(): void {
    const prior = this.chartRoll.PRIOR_YR_KEY;
    if (typeof prior === 'object' && prior && (prior as any).CHART_CODE != null) {
      const taxYearStr = String(this.filterParams?.tax_year ?? this.globalService?.globalParams?.tax_year ?? '0');
      const curr = (taxYearStr.slice(-2).replace(/^0+/, '') || '0');
      const currNum = parseInt(curr, 10) + 1;
      this.chartRoll.CURR_YR_CHART_CODE = (prior as any).CHART_CODE + '_' + currNum;
    }
    this.cdr.detectChanges();
  }

  getPriorKeyLabel(item: any): string {
    if (!item) return '';
    if (typeof item === 'object' && item.CHART_KEY != null) {
      return item.CHART_KEY + ' - ' + (item.CHART_CODE || '');
    }
    return String(item);
  }
}
